import type dxDataGrid from 'devextreme/ui/data_grid';
import type { IGridState } from 'src/app/core/models/grid-layout.models';
import type { GridStateStorageService } from 'src/app/core/services/grid-state-storage.service';

export const LIST_FILTER_COLUMN_QUERY_PARAM = 'filterColumn';
export const LIST_FILTER_VALUE_QUERY_PARAM = 'filterValue';
export const LIST_FILTER_SKIP_PERSIST_QUERY_PARAM = 'skipFilterPersist';

export const LIST_FILTER_COLUMNS = {
    customerNumber: 'buyer.clientNumber',
    customerOrderNumber: 'customerOrderNumberCollection',
    deliveryNumber: 'deliveryNumberCollection',
} as const;

export interface IListColumnFilter {
    column: string;
    value: string;
}

export interface IListFilterQueryParamsOptions {
    /** When true, the applied filter is persisted in grid layout storage. Default: false */
    persistFilter?: boolean;
}

export function buildListFilterQueryParams(column: string, value: string, options?: IListFilterQueryParamsOptions): Record<string, string> {
    const params: Record<string, string> = {
        [LIST_FILTER_COLUMN_QUERY_PARAM]: column,
        [LIST_FILTER_VALUE_QUERY_PARAM]: value,
    };

    if (!options?.persistFilter) {
        params[LIST_FILTER_SKIP_PERSIST_QUERY_PARAM] = 'true';
    }

    return params;
}

export function parseListColumnFilter(params: Record<string, string | string[] | undefined>): IListColumnFilter | null {
    const column = params[LIST_FILTER_COLUMN_QUERY_PARAM];
    const value = params[LIST_FILTER_VALUE_QUERY_PARAM];
    const columnStr = Array.isArray(column) ? column[0] : column;
    const valueStr = Array.isArray(value) ? value[0] : value;

    if (!columnStr || valueStr == null || valueStr === '') {
        return null;
    }

    return { column: columnStr, value: valueStr };
}

export function isSkipFilterPersist(params: Record<string, string | string[] | undefined>): boolean {
    const value = params[LIST_FILTER_SKIP_PERSIST_QUERY_PARAM];
    const valueStr = Array.isArray(value) ? value[0] : value;
    return valueStr === '1' || valueStr === 'true';
}

/** Query params to clear when resetting a list view (e.g. re-clicking the active nav item). */
export function clearListFilterQueryParams(): Record<string, null> {
    return {
        [LIST_FILTER_COLUMN_QUERY_PARAM]: null,
        [LIST_FILTER_VALUE_QUERY_PARAM]: null,
        [LIST_FILTER_SKIP_PERSIST_QUERY_PARAM]: null,
    };
}

const COLUMN_FILTER_KEYS = ['filterValue', 'selectedFilterOperation', 'filterValues', 'filterType'] as const;
const STATE_FILTER_KEYS = ['filtering', 'filterValue', 'filterPanel'] as const;

function omitKeys<T extends Record<string, unknown>>(obj: T, keys: readonly string[]): T {
    const copy = { ...obj };
    for (const key of keys) {
        delete copy[key];
    }
    return copy;
}

export function stripFiltersFromGridState(state: IGridState | null): IGridState | null {
    if (!state) {
        return state;
    }

    const columns = state.columns?.map(column => omitKeys(column, COLUMN_FILTER_KEYS));
    return { ...omitKeys(state, STATE_FILTER_KEYS), columns };
}

export function applyDataGridEqualsColumnFilter(grid: dxDataGrid, filter: IListColumnFilter): void {
    grid.columnOption(filter.column, 'filterValue', filter.value);
    grid.columnOption(filter.column, 'selectedFilterOperation', '=');
}

export function clearDataGridColumnFilter(grid: dxDataGrid, column: string): void {
    grid.columnOption(column, 'filterValue', null);
    grid.columnOption(column, 'selectedFilterOperation', null);
}

export function createListGridStateStorage(
    gridStateStorage: GridStateStorageService,
    key: string,
    gridVersion: number,
    navigationFilterHandler: ListNavigationFilterHandler
): ReturnType<GridStateStorageService['createStorage']> {
    const base = gridStateStorage.createStorage(key, gridVersion);

    return {
        ...base,
        customLoad: async () => {
            const state = await base.customLoad();
            if (navigationFilterHandler.shouldSkipFilterPersist()) {
                return stripFiltersFromGridState(state);
            }
            return state;
        },
        customSave: (state: IGridState) => {
            const toSave = navigationFilterHandler.shouldSkipFilterPersist() ? stripFiltersFromGridState(state) : state;
            base.customSave(toSave);
        },
    };
}

export class ListNavigationFilterHandler {
    private pendingFilter: IListColumnFilter | null = null;
    private appliedFilter: IListColumnFilter | null = null;
    private skipFilterPersist = false;

    setFilterFromQueryParams(params: Record<string, string | string[] | undefined>): void {
        this.pendingFilter = parseListColumnFilter(params);
        this.skipFilterPersist = isSkipFilterPersist(params);
    }

    shouldSkipFilterPersist(): boolean {
        return this.skipFilterPersist;
    }

    tryApply(grid: dxDataGrid | undefined): void {
        if (!grid) {
            return;
        }

        if (!this.pendingFilter) {
            if (this.appliedFilter) {
                clearDataGridColumnFilter(grid, this.appliedFilter.column);
                this.appliedFilter = null;
            }
            return;
        }

        if (this.appliedFilter?.column === this.pendingFilter.column && this.appliedFilter?.value === this.pendingFilter.value) {
            return;
        }

        if (this.appliedFilter && this.appliedFilter.column !== this.pendingFilter.column) {
            clearDataGridColumnFilter(grid, this.appliedFilter.column);
        }

        applyDataGridEqualsColumnFilter(grid, this.pendingFilter);
        this.appliedFilter = this.pendingFilter;
    }
}
